import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'

Vue.use(Vuex);

const ApiStore = {
    namespaced: true,
    state: {
        api_base: 'https://' + process.env.VUE_APP_API_BASE + '/',
    },
    getters: {
        api_base: (state) => state.api_base
    }
};

const TeacherStore = {
    namespaced: true,
    state: {
        students: [],
        lessons: [],
        validity: {
            start_date: null,
            end_date: null
        },
        dirty: false,
        trainers: [],
        class_hash: null,
        api_endpoint: null,
        initialized: false,
        print_base: 'https://' + process.env.VUE_APP_PRINT_BASE + '/',
    },
    getters: {
        class_hash: (state) => state.class_hash,
        api_endpoint: (state) => state.api_endpoint,
        api_base: (state, getters, rootState, rootGetters) => rootGetters['api/api_base'],
        print_base: (state) => state.print_base,
        trainers: (state) => state.trainers,
        students: (state) => state.students,
        lessons: (state) => state.lessons,
        validity: (state) => state.validity,
        dirty: (state) => state.dirty,
        initialized: (state) => state.initialized,
        secondary_trainers (state) {
            var trainer_ids = new Set(state.students.filter(s => s.secondary_trainer).map(s => s.secondary_trainer.id));
            var secondary_trainers = [...trainer_ids].map(id => state.students.find(s => s.secondary_trainer && s.secondary_trainer.id == id).secondary_trainer);
            secondary_trainers = [...secondary_trainers].map(t => ({trainer: t,
                                                                    students: state.students.filter(s => s.secondary_trainer && s.secondary_trainer.id == t.id)}));
            secondary_trainers.sort((a, b) => a.trainer.icon.localeCompare(b.trainer.icon));
            var no_trainer_students = state.students.filter(s => !s.secondary_trainer);

            if (no_trainer_students.length > 0) {
                secondary_trainers = secondary_trainers.concat([{trainer: null,
                                                                 students: no_trainer_students}]);
            }

            return secondary_trainers;
        }
    },
    mutations: {
        set_credentials (state, credentials) {
            state.class_hash = credentials.hash;
            state.api_endpoint = this.getters['teacher/api_base'] + 'class/' + credentials.hash;
            if (window.localStorage.getItem('hash') === null) {
                window.localStorage.setItem('type', 'teacher');
                window.localStorage.setItem('hash', credentials.hash);
            }
        },
        set_trainers (state, trainers) {
            state.trainers = trainers;
        },
        set_trainer (state, {student, trainer, secondary}) {
            var _student = state.students.find(s => s.id == student.id);
            if (secondary) {
                _student.secondary_trainer = trainer;
            } else {
                _student.trainer = trainer;
            }
        },
        set_training_group (state, {student_id, lesson_id, training}) {
            var student = state.students.find(s => s.id == student_id);
            var training_peers = state.students.filter(s => (s.trainer && s.trainer.id == student.trainer.id));
            for (var training_peer of training_peers) {
                var lesson = training_peer.lessons.find(l => l.id == lesson_id);
                lesson.state.training = training;
            }
            state.dirty = true;
        },
        set_lesson (state, {lesson_id, student_id, lesson}) {
            var student = state.students.find(s => s.id == student_id);
            var old_lesson = student.lessons.find(l => l.id == lesson_id);

            old_lesson.state.school = lesson.school;
            old_lesson.state.training = lesson.training;
            state.dirty = true;
        },
        set_lessons (state, lessons) {
            state.lessons = lessons;
        },
        set_students (state, students) {
            state.students = students;
        },
        set_validity (state, validity) {
            state.validity = validity;
        },
        set_validity_start (state, start_date) {
            state.validity.start_date = start_date;
            state.dirty = true;
        },
        set_validity_end (state, end_date) {
            state.validity.end_date = end_date;
            state.dirty = true;
        },
        set_dirty (state, dirty) {
            state.dirty = dirty;
        },
        set_student_assignments (state, {student, assignments}) {
            var _student = state.students.find(s => s.id == student.id);
            _student.assignments = assignments;
        },
        set_student_assignment (state, {student, assignment, count}) {
            var _student = state.students.find(s => s.id == student.id);
            var _assignment = _student.assignments.find(a => a.id == assignment.id);
            _assignment.count = count;
        },
        initialized (state) {
            state.initialized = true;
        }
    },
    actions: {
        async setup_credentials (context, hash) {
            await axios.get(
                context.getters['api_base'] + `class/${hash}`
            )
            context.commit('set_credentials', {hash});
        },
        async get_lessons (context) {
            var r = await axios.get(context.getters['api_endpoint'] + '/students');
            r.data.students.sort((a, b) => a.last_name.localeCompare(b.last_name));
            context.commit('set_students', r.data.students);
            context.commit('set_lessons', r.data.lessons);
            context.commit('set_validity', r.data.validity);
            context.commit('set_dirty', false);
            context.commit('initialized');
        },
        async put_lessons (context) {
            await axios.post(context.getters['api_endpoint'] + '/students', context.getters['students']);
            await axios.post(context.getters['api_endpoint'] + '/validity', {'start_date': context.getters['validity'].start_date, 'end_date': context.getters['validity'].end_date});
            context.commit('set_dirty', false);
        },
        async get_trainers (context) {
            var r = await axios.get(context.getters['api_endpoint'] + '/trainers');
            context.commit('set_trainers', r.data);
        },
        async put_trainer (context, {student, trainer, secondary}) {
            var payload = {student: {id: student.id}};
            payload[secondary ? 'secondary_trainer' : 'trainer'] = trainer;
            await axios.put(context.getters['api_endpoint'] + '/trainer', payload);

            context.commit('set_trainer', {student, trainer, secondary});
        },
        async get_assignments (context) {
            var r = await axios.get(context.getters['api_endpoint'] + '/students');
            for (var student of r.data.students) {
                context.commit('set_student_assignments', {student, assignments: student.assignments});
            }
        },
        async put_assignment (context, {student, assignment, count}) {
            try {
                var backup_count = assignment.count;
                context.commit('set_student_assignment', {student, assignment, count});
                await axios.put(
                    context.getters['api_endpoint'] + '/assignment/' + student.id + '/' + assignment.id,
                    {count: count}
                );
            } catch (e) {
                context.commit('set_student_assignment', {student, assignment, count: backup_count});
            }
        },
        async post_assignment_topic (context, assignment_topic) {
            await axios.post(context.getters['api_endpoint'] + '/assignment_topic', assignment_topic);
            await context.dispatch('get_assignments');
        },
        async put_assignment_topic (context, assignment_topic) {
            await axios.put(context.getters['api_endpoint'] + '/assignment_topic', assignment_topic);
            await context.dispatch('get_assignments');
        },
        async delete_assignment_topic (context, assignment_topic) {
            await axios.delete(context.getters['api_endpoint'] + '/assignment_topic/' + assignment_topic.id);
            await context.dispatch('get_assignments');
        }
    },
    modules: {
    }
};

const TrainerStore = {
    namespaced: true,
    state: {
      students: [],
      lessons: [],
      events: [],
      event_types: [],
      trainer_hash: null,
      api_endpoint: null
    },
    getters: {
      students: (state) => state.students,
      lessons: (state) => state.lessons,
      events: (state) => state.events,
      event_types: (state) => state.event_types,
      trainer_hash: (state) => state.trainer_hash,
      api_base: (state, getters, rootState, rootGetters) => rootGetters['api/api_base'],
      api_endpoint: (state) => state.api_endpoint,
    },
    mutations: {
      set_credentials (state, credentials) {
          state.trainer_hash = credentials.hash
          state.api_endpoint = this.getters['trainer/api_base'] + 'trainer/' + credentials.hash;
          if (window.localStorage.getItem('hash') === null) {
              window.localStorage.setItem('type', 'trainer');
              window.localStorage.setItem('hash', credentials.hash);
          }
      },
      set_students (state, students) {
          state.students = students
      },
      set_lessons (state, lessons) {
          state.lessons = lessons
      },
      set_events (state, events) {
          state.events = events
      },
      set_event (state, event) {
          state.events = state.events.map((e) => {
              if (event.id == e.id) {
                  return event
              } else {
                  return e
              }
          })
      },
      set_event_types (state, event_types) {
          state.event_types = event_types
      },
    },
    actions: {
        async setup_credentials (context, hash) {
            await axios.get(
                context.getters['api_base'] + `trainer/${hash}`
            )
            context.commit('set_credentials', {hash});
        },
        async get_lessons (context) {
            var r = await axios.get(context.getters['api_endpoint'] + '/students');
            r.data.students.sort((a, b) => a.last_name.localeCompare(b.last_name));
            context.commit('set_students', r.data.students);
            context.commit('set_lessons', r.data.lessons);
        },
        async get_events (context) {
            var r = await axios.get(context.getters['api_endpoint'] + '/events');
            context.commit('set_events', r.data.events);
            context.commit('set_event_types', r.data.types);
        },
        async post_event (context, event) {
            var r = await axios.post(context.getters['api_endpoint'] + '/event', event);
            event['id'] = r.data.id;
            var events = context.getters['events'].concat([event]);
            context.commit('set_events', events);
        },
        async put_event (context, event) {
            await axios.put(context.getters['api_endpoint'] + '/event', event);
            context.commit('set_event', event);
        },
        async delete_event (context, event) {
            await axios.delete(context.getters['api_endpoint'] + `/event/${event.id}`, event);
            var events = context.getters['events'].filter((e) => e.id != event.id);
            context.commit('set_events', events);
        }
    },
    modules: {
    }
}

export default new Vuex.Store({
    modules: {
        teacher: TeacherStore,
        trainer: TrainerStore,
        api: ApiStore
    }
});
